import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzCodeEditorModule } from 'ng-zorro-antd/code-editor';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { ISablon, ISablonOlaylar } from '../sablon-editoru/sablon.interface';

interface IOlayOneri {
  etiket: string;
  kod: string;
}

const OLAY_ONERILERI: Record<string, IOlayOneri[]> = {
  tiklanma: [
    { etiket: 'Yönlendirme', kod: "router.navigate(['/path'])" },
    { etiket: 'Bildirim', kod: "bildirim('Mesaj')" },
    { etiket: 'Güncelle', kod: 'guncelle({key: value})' },
  ],
  yenilendi: [
    { etiket: 'Eşik Kontrolü', kod: "if (veri.x > 100) bildirim('Eşik aşıldı')" },
  ],
  esikAsimi: [
    { etiket: 'Uyarı', kod: "bildirim('Dikkat: ' + veri.deger)" },
  ],
  ciftTiklama: [
    { etiket: 'Aç', kod: 'window.open(veri.url)' },
  ],
};

const OLAY_TURLERI: { key: string; baslik: string }[] = [
  { key: 'tiklanma', baslik: 'Tıklanma' },
  { key: 'yenilendi', baslik: 'Yenilendiğinde' },
  { key: 'esikAsimi', baslik: 'Eşik Aşımı' },
  { key: 'ciftTiklama', baslik: 'Çift Tıklama' },
];

@Component({
  selector: 'te-kod-paneli',
  standalone: true,
  imports: [CommonModule, FormsModule, NzCodeEditorModule, NzTabsModule, NzButtonModule, NzIconModule, NzSelectModule, NzTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="kod-paneli">
      <nz-tabs
        nzSize="small"
        nzType="card"
        [nzSelectedIndex]="tabIndex()"
        (nzSelectedIndexChange)="tabDegisti($event)">

        <nz-tab>
          <span *nzTabLink><span nz-icon nzType="code"></span> HTML</span>
          <div class="editor-sarmal">
            <nz-code-editor
              [ngModel]="sablon()?.htmlIcerik ?? ''"
              (ngModelChange)="htmlDegisti.emit($event)"
              [nzEditorOption]="htmlOptions">
            </nz-code-editor>
          </div>
        </nz-tab>

        <nz-tab>
          <span *nzTabLink><span nz-icon nzType="bg-colors"></span> CSS</span>
          <div class="editor-sarmal">
            <nz-code-editor
              [ngModel]="sablon()?.cssIcerik ?? ''"
              (ngModelChange)="cssDegisti.emit($event)"
              [nzEditorOption]="cssOptions">
            </nz-code-editor>
          </div>
        </nz-tab>

        <nz-tab>
          <span *nzTabLink><span nz-icon nzType="thunderbolt"></span> JS</span>
          <div class="editor-sarmal">
            <nz-code-editor
              [ngModel]="sablon()?.jsIcerik ?? ''"
              (ngModelChange)="jsDegisti.emit($event)"
              [nzEditorOption]="jsOptions">
            </nz-code-editor>
          </div>
        </nz-tab>

        <nz-tab>
          <span *nzTabLink><span nz-icon nzType="api"></span> Olaylar</span>
          <div class="olaylar-sarmal">
            @for (olay of olayTurleri; track olay.key) {
              <div class="olay-bolum">
                <div class="olay-baslik">{{ olay.baslik }}</div>
                <div class="olay-onerileri">
                  @for (oneri of olayOnerileri[olay.key]; track oneri.etiket) {
                    <button nz-button nzSize="small" nzType="dashed"
                      (click)="oneriEkle(olay.key, oneri.kod)">
                      {{ oneri.etiket }}
                    </button>
                  }
                </div>
                <div class="olay-editor">
                  <nz-code-editor
                    [ngModel]="olayDegeri(olay.key)"
                    (ngModelChange)="olayKoduDegisti(olay.key, $event)"
                    [nzEditorOption]="jsOptions">
                  </nz-code-editor>
                </div>
              </div>
            }
          </div>
        </nz-tab>
      </nz-tabs>

      <button class="kucult-btn" nz-button nzSize="small" nzType="text"
        (click)="kucultTiklandi.emit()"
        [nz-tooltip]="kucuk() ? 'Paneli Aç' : 'Paneli Küçült'">
        <span nz-icon [nzType]="kucuk() ? 'up' : 'down'" nzTheme="outline"></span>
      </button>
    </div>
  `,
  styles: `
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    .kod-paneli {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: var(--te-kod-bg, rgba(0, 0, 0, 0.03));
      border-radius: 6px;
      overflow: hidden;
      position: relative;
    }

    :host ::ng-deep .ant-tabs {
      display: flex;
      flex-direction: column;
      height: 100%;
    }

    :host ::ng-deep .ant-tabs-nav {
      margin: 0;
      padding: 0 8px;
      background: var(--te-kod-nav-bg, rgba(0, 0, 0, 0.04));
    }

    :host ::ng-deep .ant-tabs-tab {
      padding: 6px 12px !important;
      font-size: 12px;
      color: var(--te-text-secondary, rgba(0, 0, 0, 0.55));
      border: none !important;
      background: transparent !important;
      transition: color 0.2s;
    }

    :host ::ng-deep .ant-tabs-tab-active .ant-tabs-tab-btn {
      color: #1890ff !important;
    }

    :host ::ng-deep .ant-tabs-tab-active {
      border-bottom: 2px solid #1890ff !important;
    }

    :host ::ng-deep .ant-tabs-content-holder,
    :host ::ng-deep .ant-tabs-content,
    :host ::ng-deep .ant-tabs-tabpane {
      height: 100%;
      flex: 1;
      min-height: 0;
    }

    :host ::ng-deep .ant-tabs-ink-bar { background: #1890ff; }

    .editor-sarmal { height: 100%; overflow: hidden; }
    .editor-sarmal nz-code-editor { height: 100%; }
    .olaylar-sarmal { height: 100%; overflow-y: auto; padding: 8px; }
    .olay-bolum { margin-bottom: 16px; }

    .olay-baslik {
      font-size: 12px;
      font-weight: 600;
      color: var(--py-metin, rgba(0, 0, 0, 0.85));
      margin-bottom: 6px;
      padding: 4px 8px;
      background: var(--te-hover, rgba(0, 0, 0, 0.04));
      border-radius: 4px;
    }

    .olay-onerileri { display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 6px; }

    .olay-onerileri button {
      font-size: 11px;
      color: var(--te-text-secondary, rgba(0, 0, 0, 0.45));
      border-color: var(--te-border, rgba(0, 0, 0, 0.12));
    }

    .olay-onerileri button:hover { color: #1890ff; border-color: #1890ff; }

    .olay-editor { height: 180px; border-radius: 4px; overflow: hidden; }
    .olay-editor nz-code-editor { height: 100%; }

    .kucult-btn {
      position: absolute;
      bottom: 4px;
      right: 4px;
      color: var(--te-text-muted, rgba(0, 0, 0, 0.35));
      z-index: 5;
    }

    .kucult-btn:hover {
      color: var(--te-text-secondary, rgba(0, 0, 0, 0.65));
    }

    :host-context(body.tema-karanlik) {
      --te-kod-bg: rgba(0, 0, 0, 0.3);
      --te-kod-nav-bg: rgba(0, 0, 0, 0.2);
      --te-text-secondary: rgba(255, 255, 255, 0.6);
      --te-text-muted: rgba(255, 255, 255, 0.4);
      --te-hover: rgba(255, 255, 255, 0.06);
      --te-border: rgba(255, 255, 255, 0.15);
    }
  `,
})
export class TeKodPaneliComponent {
  sablon = input<ISablon>();
  kucuk = input(false);
  aktifTab = signal<'html' | 'css' | 'js' | 'olaylar'>('html');

  htmlDegisti = output<string>();
  cssDegisti = output<string>();
  jsDegisti = output<string>();
  olaylarDegisti = output<ISablonOlaylar>();
  kucultTiklandi = output<void>();

  olayTurleri = OLAY_TURLERI;
  olayOnerileri = OLAY_ONERILERI;

  private tabSirasi: ('html' | 'css' | 'js' | 'olaylar')[] = ['html', 'css', 'js', 'olaylar'];

  tabIndex = computed(() => this.tabSirasi.indexOf(this.aktifTab()));

  private get editorTema(): string {
    return document.body.classList.contains('tema-karanlik') ? 'vs-dark' : 'vs';
  }

  private get editorTemelSecenekler() {
    return {
      theme: this.editorTema,
      minimap: { enabled: false },
      lineNumbers: 'on' as const,
      scrollBeyondLastLine: false,
      automaticLayout: true,
      fontSize: 13,
      tabSize: 2,
    };
  }

  get htmlOptions() { return { ...this.editorTemelSecenekler, language: 'html' }; }
  get cssOptions() { return { ...this.editorTemelSecenekler, language: 'css' }; }
  get jsOptions() { return { ...this.editorTemelSecenekler, language: 'javascript' }; }

  tabDegisti(index: number) {
    this.aktifTab.set(this.tabSirasi[index]);
  }

  olayDegeri(key: string): string {
    return this.sablon()?.olaylar?.[key] ?? '';
  }

  olayKoduDegisti(key: string, kod: string) {
    const mevcut = this.sablon()?.olaylar ?? {};
    this.olaylarDegisti.emit({ ...mevcut, [key]: kod });
  }

  oneriEkle(key: string, kod: string) {
    const mevcut = this.olayDegeri(key);
    const yeni = mevcut ? mevcut + '\n' + kod : kod;
    this.olayKoduDegisti(key, yeni);
  }
}
